import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTenant } from "./context/TenantContext";
import { useAuth } from "./context/AuthContext";
import { useCart } from "./context/CartContext";
import { usePaymentCharges } from "./hooks/usePaymentCharges";
import GoogleAuthButton from "./features/shop/GoogleAuthButton";
import { MyTicketsList } from "./features/shop/MyTickets";
import MemberExpenses from "./features/member/MemberExpenses";
import PayoutDetailsCard from "./features/member/PayoutDetailsCard";
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Wallet, QrCode, RefreshCw, CreditCard, TrendingDown, History, Calendar, Download, LogOut, Key, Plus, Loader2, Heart, Users, Unlink, XCircle, Edit2, MapPin, Phone, Search, Clock, CheckCircle, HelpCircle, Ticket, ShoppingCart, Info, ShieldCheck, BadgeCheck, Receipt } from "lucide-react";
import QRCode from "react-qr-code";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const _slug = () => window.location.pathname.split('/')[1] || 'mymaitree';

// Password Input Component
const PasswordInput = ({ value, onChange, placeholder, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input 
        type={show ? "text" : "password"} 
        value={value} 
        onChange={onChange} 
        placeholder={placeholder} 
        className="w-full p-4 pr-12 rounded-2xl border bg-white" 
        data-testid={testId} 
      />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
};

// Step indicator component
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {steps.map((step, i) => (
      <div key={i} className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
          i + 1 === currentStep ? "bg-[#1e3a5f] text-white" : 
          i + 1 < currentStep ? "bg-green-500 text-white" : "bg-slate-200 text-slate-400"
        }`}>
          {i + 1 < currentStep ? "✓" : i + 1}
        </div>
        {i < steps.length - 1 && <div className={`w-12 h-1 ${i + 1 < currentStep ? "bg-green-500" : "bg-slate-200"}`} />}
      </div>
    ))}
  </div>
);

// Registration Page
export const WalletRegister = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const googlePrefill = location.state?.google || null;
  // Honour ?next=<path> across the whole register flow (email/OTP/password + Google path)
  // so users who were bounced here from checkout end up back at checkout after signup.
  const nextRaw = searchParams.get("next");
  const nextParam = (nextRaw && nextRaw.startsWith("/") && !nextRaw.startsWith("//")) ? nextRaw : null;
  const [step, setStep] = useState(1);
  const [name, setName] = useState(googlePrefill?.name || "");
  const [email, setEmail] = useState(googlePrefill?.email || "");
  const [dob, setDob] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login: authLoginRegister } = useAuth();

  const handleGoogleGuestSubmit = async (e) => {
    e.preventDefault();
    if (!name || !dob) return toast.error("Please fill your name and date of birth");
    if (new Date(dob) >= new Date(new Date().setHours(0,0,0,0))) return toast.error("Date of birth cannot be in the future");
    if (!gdprConsent) return toast.error("You must agree to the privacy policy");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/wallet/complete-google-registration`, {
        role: "guest",
        google_reg_token: googlePrefill.google_reg_token || googlePrefill.google_session_token,
        email, name, dob,
        consent_marketing: gdprConsent,
      });
      authLoginRegister(res.data.token, {
        name: res.data.name, email: res.data.email,
        customer_id: res.data.customer_id, role: "guest",
        must_change_password: false,
      });
      toast.success("Welcome! Your account is ready.");
      // Return to where the user came from (typically /{slug}/checkout after a shop redirect).
      navigate(nextParam || "/" + _slug() + "/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !dob) return toast.error("Please fill all fields");
    if (new Date(dob) >= new Date(new Date().setHours(0,0,0,0))) return toast.error("Date of birth cannot be in the future");
    if (!gdprConsent) return toast.error("You must agree to the privacy policy");
    
    setLoading(true);
    try {
      await axios.post(`${API}/wallet/register`, { name, email, dob, gdpr_consent: gdprConsent });
      toast.success("Verification code sent to your email");
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) return toast.error("Please enter the 6-digit code");
    
    setLoading(true);
    try {
      await axios.post(`${API}/wallet/verify-email`, { email, code });
      toast.success("Email verified!");
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirmPassword) return toast.error("Passwords don't match");
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/wallet/set-password`, { email, code, password });
      toast.success("Account created! Your digital wallet card has been sent to your email.");
      // Preserve ?next= across the register → login hop so the user lands back
      // on their intended destination (e.g. /{slug}/checkout) after logging in.
      const loginUrl = "/" + _slug() + "/login" + (nextParam ? `?next=${encodeURIComponent(nextParam)}` : "");
      navigate(loginUrl);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to set password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-slate-100" data-testid="wallet-register">
      <button onClick={() => navigate("/" + _slug() )} className="absolute top-6 left-6 p-2 rounded-full hover:bg-white/50">
        <ArrowLeft className="w-6 h-6 text-slate-600" />
      </button>
      
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/mymaitree-logo.png" alt="My Maitree" className="w-32 mx-auto mb-4" onError={(e) => { e.target.style.display = 'none'; }} />
          <h1 className="text-2xl font-extrabold text-[#1e3a5f]">Guest Registration</h1>
          <p className="text-slate-500 mt-1">
            {step === 1 && "Enter your details to get started"}
            {step === 2 && "Check your email for verification code"}
            {step === 3 && "Set your password to secure your account"}
          </p>
        </div>

        <StepIndicator currentStep={step} steps={["Details", "Verify", "Password"]} />

        {googlePrefill && step === 1 && (
          <form onSubmit={handleGoogleGuestSubmit} className="bg-white rounded-3xl p-6 shadow-sm space-y-4" data-testid="google-guest-form">
            <div className="p-3 rounded-xl bg-blue-50 text-blue-800 text-xs">
              ✓ Signed in with Google as <strong>{email}</strong>. Fill the remaining details to complete guest registration.
            </div>
            <div>
              <label className="text-sm text-slate-500 mb-1 block">
                Full Name
                {googlePrefill?.name && <span className="text-blue-600 text-xs ml-2">(from Google)</span>}
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={!!googlePrefill?.name}
                className={`w-full p-4 rounded-2xl border ${googlePrefill?.name ? "bg-slate-50 text-slate-500 cursor-not-allowed" : "bg-white"}`}
                data-testid="google-guest-name"
              />
            </div>
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Date of Birth (must be 18+)</label>
              <input type="date" max={new Date().toISOString().slice(0,10)} value={dob} onChange={e => setDob(e.target.value)} className="w-full p-4 rounded-2xl border bg-white" data-testid="google-guest-dob" />
            </div>
            <label className="flex items-start gap-3 p-4 bg-slate-50 rounded-2xl cursor-pointer">
              <input type="checkbox" checked={gdprConsent} onChange={e => setGdprConsent(e.target.checked)} className="mt-1" />
              <span className="text-xs text-slate-600">I agree to the <a href="https://maitree-ev.org/privacy-statement-eu/?cmplz_region_redirect=true" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] font-semibold underline">privacy policy</a> and consent to marketing emails.</span>
            </label>
            <button type="submit" disabled={loading} className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50" data-testid="google-guest-submit">
              {loading ? "Creating..." : "Complete Registration"}
            </button>
          </form>
        )}

        {!googlePrefill && step === 1 && (
          <form onSubmit={handleRegister} className="bg-white rounded-3xl p-6 shadow-sm space-y-4">
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Full Name</label>
              <input 
                type="text" 
                value={name} 
                onChange={e => setName(e.target.value)} 
                placeholder="Enter your full name" 
                className="w-full p-4 rounded-2xl border bg-white" 
                data-testid="register-name"
              />
            </div>
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Email Address</label>
              <input 
                type="email" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                placeholder="Enter your email" 
                className="w-full p-4 rounded-2xl border bg-white" 
                data-testid="register-email"
              />
            </div>
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Date of Birth (must be 18+)</label>
              <input 
                type="date" 
                max={new Date().toISOString().slice(0,10)}
                value={dob} 
                onChange={e => setDob(e.target.value)} 
                className="w-full p-4 rounded-2xl border bg-white" 
                data-testid="register-dob"
              />
            </div>
            <label className="flex items-start gap-3 p-4 bg-slate-50 rounded-2xl cursor-pointer">
              <input 
                type="checkbox" 
                checked={gdprConsent} 
                onChange={e => setGdprConsent(e.target.checked)} 
                className="mt-1"
                data-testid="gdpr-checkbox"
              />
              <span className="text-sm text-slate-600">
                I agree to the <a href="https://maitree-ev.org/privacy-statement-eu/?cmplz_region_redirect=true" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline">Privacy Policy</a> and consent to the processing of my personal data in accordance with GDPR.
              </span>
            </label>
            <button 
              type="submit" 
              disabled={loading} 
              className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50"
              data-testid="register-submit"
            >
              {loading ? "Creating..." : "Continue"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleVerify} className="bg-white rounded-3xl p-6 shadow-sm space-y-4">
            <div className="text-center mb-4">
              <Mail className="w-12 h-12 text-[#1e3a5f] mx-auto mb-2" />
              <p className="text-slate-600">We sent a verification code to</p>
              <p className="font-bold text-[#1e3a5f]">{email}</p>
            </div>
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Verification Code</label>
              <input 
                type="text" 
                value={code} 
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                placeholder="Enter 6-digit code" 
                className="w-full p-4 rounded-2xl border bg-white text-center text-2xl tracking-widest font-mono" 
                maxLength={6}
                data-testid="verify-code"
              />
            </div>
            <button 
              type="submit" 
              disabled={loading} 
              className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50"
              data-testid="verify-submit"
            >
              {loading ? "Verifying..." : "Verify Email"}
            </button>
            <button 
              type="button" 
              onClick={() => setStep(1)} 
              className="w-full py-2 text-slate-500 text-sm hover:underline"
            >
              Wrong email? Go back
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleSetPassword} className="bg-white rounded-3xl p-6 shadow-sm space-y-4">
            <div className="text-center mb-4">
              <Lock className="w-12 h-12 text-[#1e3a5f] mx-auto mb-2" />
              <p className="text-slate-600">Create a secure password</p>
            </div>
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Password</label>
              <PasswordInput 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                placeholder="Enter password (min 6 characters)" 
                testId="set-password"
              />
            </div>
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Confirm Password</label>
              <PasswordInput 
                value={confirmPassword} 
                onChange={e => setConfirmPassword(e.target.value)} 
                placeholder="Confirm password" 
                testId="confirm-password"
              />
            </div>
            <button 
              type="submit" 
              disabled={loading} 
              className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50"
              data-testid="password-submit"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>
        )}

        {!googlePrefill && (
          <>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>
            <GoogleAuthButton tenantSlug={_slug()} label="Register with Google" />
          </>
        )}

        <p className="text-center text-slate-500 text-sm mt-6">
          Already have an account? <a href={"/" + _slug() + "/login"} className="text-[#1e3a5f] font-bold hover:underline">Login</a>
        </p>
      </div>
    </div>
  );
};

// Login Page
export const WalletLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);
  // First-login password flow
  const [needsPassword, setNeedsPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPw, setConfirmNewPw] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  const [approvedName, setApprovedName] = useState("");
  // Verify email flow
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [verifyEmailAddr, setVerifyEmailAddr] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [verifySent, setVerifySent] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: authLogin } = useAuth();

  // Auto-detect "set password" flow from email link
  useEffect(() => {
    const setupEmail = searchParams.get("setup");
    if (setupEmail) {
      setEmail(setupEmail);
      // Auto-check if this member needs to set password
      (async () => {
        try {
          const res = await axios.post(`${API}/wallet/login`, { email: setupEmail, password: "__check__" });
          if (res.data.status === "needs_password") {
            setNeedsPassword(true);
            setApprovedName(res.data.name || "");
          }
        } catch {
          // If login fails, just show the normal login form with email pre-filled
        }
      })();
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Please fill all fields");
    setPendingMessage(null);
    setNeedsPassword(false);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/wallet/login`, { email, password });
      // Check if member needs to set password on first login
      if (res.data.status === "needs_password") {
        setNeedsPassword(true);
        setApprovedName(res.data.name || "");
        setLoading(false);
        return;
      }
      const userObj = {
        name: res.data.name,
        email,
        customer_id: res.data.customer_id,
        role: res.data.role || "guest",
        member_type_id: res.data.member_type_id || null,
        must_change_password: res.data.must_change_password || false,
        pending_member_approval: res.data.pending_member_approval || false,
      };
      authLogin(res.data.token, userObj);
      if (res.data.must_change_password) {
        toast.info("Please change your password to continue");
      } else if (res.data.pending_member_approval) {
        toast.info("Your membership application is pending approval");
      } else {
        toast.success("Login successful");
      }
      // Honour ?next=<path> to return the user where they came from (e.g. an event page during checkout).
      const nextRaw = searchParams.get("next");
      if (nextRaw && nextRaw.startsWith("/") && !nextRaw.startsWith("//")) {
        navigate(nextRaw);
      } else {
        navigate("/" + _slug() + "/dashboard");
      }
    } catch (e) {
      if (e.response?.status === 403) {
        setPendingMessage(e.response?.data?.detail);
      } else {
        toast.error(e.response?.data?.detail || "Invalid credentials");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetFirstPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters");
    if (newPassword !== confirmNewPw) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await axios.post(`${API}/members/set-first-password`, { email, password: newPassword });
      toast.success("Password set! Logging you in...");
      // Auto-login with new password
      const res = await axios.post(`${API}/wallet/login`, { email, password: newPassword });
      authLogin(res.data.token, {
        name: res.data.name,
        email,
        customer_id: res.data.customer_id,
        role: res.data.role || "member",
        must_change_password: false,
      });
      const nextRaw = searchParams.get("next");
      if (nextRaw && nextRaw.startsWith("/") && !nextRaw.startsWith("//")) {
        navigate(nextRaw);
      } else {
        navigate("/" + _slug() + "/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set password");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) return toast.error("Please enter your email");
    setForgotLoading(true);
    try {
      await axios.post(`${API}/wallet/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("If this email is registered, a temporary password has been sent.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verifyEmailAddr) return toast.error("Please enter your email");
    setVerifyLoading(true);
    try {
      await axios.post(`${API}/wallet/resend-verification`, { email: verifyEmailAddr });
      setVerifySent(true);
      toast.success("Verification code sent to your email");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pending registration found for this email");
    } finally { setVerifyLoading(false); }
  };

  const handleVerifyFromLogin = async () => {
    if (!verifyCode) return toast.error("Enter the verification code");
    setVerifyLoading(true);
    try {
      // Try guest verification first
      await axios.post(`${API}/wallet/verify-email`, { email: verifyEmailAddr, code: verifyCode });
      toast.success("Email verified! Please complete your registration.");
      navigate("/" + _slug() + "/login");
    } catch {
      try {
        // Try member verification
        await axios.post(`${API}/members/self-verify-email`, { email: verifyEmailAddr, code: verifyCode });
        toast.success("Email verified! Please complete your registration.");
        navigate(`/${_slug()}/member-register?email=${encodeURIComponent(verifyEmailAddr)}`);
      } catch (e2) {
        const detail = e2.response?.data?.detail || "Verification failed";
        if (detail.includes("expired")) {
          toast.error("Code expired. Please register again.");
          setShowVerifyEmail(false);
        } else {
          toast.error(detail);
        }
      }
    } finally { setVerifyLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-slate-50 to-slate-100" data-testid="wallet-login">
      <button onClick={() => (showForgot || showVerifyEmail) ? (setShowForgot(false), setShowVerifyEmail(false)) : navigate("/" + _slug() )} className="absolute top-6 left-6 p-2 rounded-full hover:bg-white/50">
        <ArrowLeft className="w-6 h-6 text-slate-600" />
      </button>
      
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/mymaitree-logo.png" alt="My Maitree" className="w-32 mx-auto mb-4" onError={(e) => { e.target.style.display = 'none'; }} />
          <p className="text-slate-500 mt-1">{showForgot ? "Reset your password" : showVerifyEmail ? "Verify your email" : "Login to your account"}</p>
        </div>

        {pendingMessage && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4" data-testid="pending-approval-msg">
            <p className="text-amber-800 text-sm font-medium text-center">{pendingMessage}</p>
          </div>
        )}

        {/* Set first password form (for newly approved members) */}
        {needsPassword ? (
          <div className="bg-white rounded-3xl p-6 shadow-sm" data-testid="set-first-password-form">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-5">
              <p className="text-green-800 text-sm font-bold">Welcome{approvedName ? `, ${approvedName}` : ""}!</p>
              <p className="text-green-700 text-xs mt-1">Your membership has been approved. Please set a password to access your account.</p>
            </div>
            <form onSubmit={handleSetFirstPassword} className="space-y-4">
              <div>
                <label className="text-sm text-slate-500 mb-1 block">New Password *</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-4 w-4 h-4 text-slate-400" />
                  <input
                    type={showNewPw ? "text" : "password"}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    minLength={8}
                    required
                    className="w-full pl-11 pr-11 py-3.5 rounded-2xl border focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
                    data-testid="first-password-input"
                  />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-4 top-4">
                    {showNewPw ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-500 mb-1 block">Confirm Password *</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-4 w-4 h-4 text-slate-400" />
                  <input
                    type={showNewPw ? "text" : "password"}
                    value={confirmNewPw}
                    onChange={e => setConfirmNewPw(e.target.value)}
                    placeholder="Repeat your password"
                    required
                    className="w-full pl-11 pr-4 py-3.5 rounded-2xl border focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]"
                    data-testid="first-password-confirm-input"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50"
                data-testid="set-first-password-btn"
              >
                {loading ? "Setting up..." : "Set Password & Login"}
              </button>
            </form>
          </div>
        ) : showForgot ? (          <div className="bg-white rounded-3xl p-6 shadow-sm space-y-4" data-testid="forgot-password-form">
            {forgotSent ? (
              <div className="text-center py-4">
                <Mail className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-slate-700 font-medium mb-2">Check your email</p>
                <p className="text-slate-500 text-sm">If <strong>{forgotEmail}</strong> is registered, we've sent a temporary password.</p>
                <button
                  onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(""); }}
                  className="mt-6 w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold"
                  data-testid="back-to-login-btn"
                >
                  Back to Login
                </button>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-sm text-slate-500 mb-1 block">Email Address</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    className="w-full p-4 rounded-2xl border bg-white"
                    data-testid="forgot-email-input"
                  />
                </div>
                <button
                  onClick={handleForgotPassword}
                  disabled={forgotLoading}
                  className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? "Sending..." : "Send Temporary Password"}
                </button>
              </>
            )}
          </div>
        ) : showVerifyEmail ? (
          <div className="bg-white rounded-3xl p-6 shadow-sm space-y-4" data-testid="verify-email-form">
            {!verifySent ? (
              <>
                <p className="text-slate-600 text-sm">Enter the email you used during registration to get a new verification code.</p>
                <div>
                  <label className="text-sm text-slate-500 mb-1 block">Email Address</label>
                  <input type="email" value={verifyEmailAddr} onChange={e => setVerifyEmailAddr(e.target.value)} placeholder="Enter your email" className="w-full p-4 rounded-2xl border bg-white" data-testid="verify-email-input" />
                </div>
                <button onClick={handleResendVerification} disabled={verifyLoading} className="w-full py-4 rounded-full bg-amber-500 text-white font-bold disabled:opacity-50" data-testid="verify-send-btn">
                  {verifyLoading ? "Sending..." : "Send Verification Code"}
                </button>
                <button onClick={() => setShowVerifyEmail(false)} className="w-full py-2 text-sm text-slate-500 hover:underline">Back to Login</button>
              </>
            ) : (
              <>
                <p className="text-slate-600 text-sm">Enter the 6-digit code sent to <strong>{verifyEmailAddr}</strong></p>
                <input value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="------" maxLength={6} className="w-full px-4 py-3 rounded-xl border text-center text-2xl tracking-[0.5em] font-mono" data-testid="verify-code-input" />
                <button onClick={handleVerifyFromLogin} disabled={verifyLoading} className="w-full py-4 rounded-full bg-green-600 text-white font-bold disabled:opacity-50" data-testid="verify-code-submit">
                  {verifyLoading ? "Verifying..." : "Verify Email"}
                </button>
                <button onClick={() => { setVerifySent(false); setVerifyCode(""); }} className="w-full py-2 text-sm text-slate-500 hover:underline">Resend Code</button>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={handleLogin} className="bg-white rounded-3xl p-6 shadow-sm space-y-4">
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Email Address</label>
              <input 
                type="email" 
                value={email} 
                onChange={e => setEmail(e.target.value)} 
                placeholder="Enter your email" 
                className="w-full p-4 rounded-2xl border bg-white" 
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="text-sm text-slate-500 mb-1 block">Password</label>
              <PasswordInput 
                value={password} 
                onChange={e => setPassword(e.target.value)} 
                placeholder="Enter your password" 
                testId="login-password"
              />
            </div>
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setShowVerifyEmail(true)}
                className="text-sm text-amber-600 hover:underline"
                data-testid="verify-email-link"
              >
                Verify Email
              </button>
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="text-sm text-[#1e3a5f] hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </button>
            </div>
            <button 
              type="submit" 
              disabled={loading} 
              className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? "Logging in..." : "Login"}
            </button>
          </form>
        )}

        {!showForgot && !showVerifyEmail && (
          <>
            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>
            <GoogleAuthButton tenantSlug={_slug()} />

            <p className="text-center text-slate-500 text-sm mt-6">
              Don't have an account? <a href={"/" + _slug() + "/register" + (searchParams.get("next") ? `?next=${encodeURIComponent(searchParams.get("next"))}` : "")} className="text-[#1e3a5f] font-bold hover:underline">Register</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// Dashboard Page
export const WalletDashboard = () => {
  const cart = useCart();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("wallet");
  const [txSearch, setTxSearch] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [forceChangePassword, setForceChangePassword] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmNewPw, setConfirmNewPw] = useState("");
  const [changePwLoading, setChangePwLoading] = useState(false);
  const [topupAmount, setTopupAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [topupLoading, setTopupLoading] = useState(false);
  const [topupPolling, setTopupPolling] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, onConfirm }
  const navigate = useNavigate();
  const { hasModule } = useTenant();
  const isLoggingOut = useRef(false);

  const walletToken = localStorage.getItem("wallet_token");
  const walletUser = JSON.parse(localStorage.getItem("wallet_user") || "{}");

  useEffect(() => {
    if (isLoggingOut.current) return; // suppress guard during intentional logout
    if (!walletToken) {
      navigate("/" + _slug() + "/login");
      return;
    }
    // Check if must change password
    if (walletUser.must_change_password) {
      setForceChangePassword(true);
      setShowChangePassword(true);
    }
    fetchDashboard();
    
    // Check for Stripe return
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("topup_session_id");
    if (sessionId) {
      setTab("topup");
      setTopupPolling(true);
      pollCheckoutStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("topup_cancelled")) {
      toast.info("Top-up cancelled");
      setTab("topup");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // Handle family member payment return
    const fmSessionId = params.get("fm_payment_session");
    if (fmSessionId) {
      setTab("membership");
      pollFmPayment(fmSessionId);
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("fm_payment_cancelled")) {
      toast.info("Family member payment cancelled");
      setTab("membership");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [walletToken]);

  useEffect(() => {
    // Refetch on tab focus / visibility change so a member who was just
    // converted-to-guest (or approved / cancelled) by the FA sees the
    // updated wallet state without having to log out. The backend
    // transparently rotates the auth cookie when the identity flips.
    // Silent + throttled: never show the full-page loader (it unmounts half-filled forms).
    let last = Date.now();
    const refresh = () => { if (Date.now() - last > 30000) { last = Date.now(); fetchDashboard({ silent: true }); } };
    const onFocus = () => refresh();
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboard = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/dashboard`, {
        headers: { Authorization: `Bearer ${walletToken}` }
      });
      setData(res.data);
      // Fetch family members if user is a member
      if (res.data.is_member) {
        fetchFamilyMembers();
      }
    } catch (e) {
      // 401 → JWT expired/invalid. 404 → JWT still valid but the referenced
      // member/guest no longer exists (e.g. after a data purge/reseed). Both
      // are unrecoverable without re-authentication, so treat them the same.
      const status = e.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        authLogout();
        toast.error(status === 404 ? "Your session is out of date. Please login again." : "Session expired. Please login again.");
        navigate("/" + _slug() + "/login");
      } else if (!silent) {
        toast.error("Failed to load dashboard");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchFamilyMembers = async () => {
    try {
      const res = await axios.get(`${API}/wallet/family-members`, {
        headers: { Authorization: `Bearer ${walletToken}` }
      });
      setFamilyMembers(res.data || []);
    } catch {
      // silently fail - family members section just won't show
    }
  };

  const { logout: authLogout } = useAuth();

  const handleLogout = () => {
    // Suppress the walletToken auth-guard useEffect so React doesn't race us to /login
    isLoggingOut.current = true;
    navigate("/" + _slug(), { replace: true });
    authLogout();
  };

  // ---- Stripe Top-Up ----
  const PACKAGES = [
    { id: "5", amount: 5 },
    { id: "10", amount: 10 },
    { id: "20", amount: 20 },
    { id: "50", amount: 50 },
  ];
  // Fee is fetched from the FA-configured "Payment Processing Charges" setting
  // so previews always match what the backend actually charges. Audience is
  // resolved from the payload we already have (`data.is_member`) so any FA
  // override for members vs guests is applied consistently.
  const paymentAudience = data?.is_member ? "member" : "guest";
  const { computeFee: calcFee, label: chargesLabel } = usePaymentCharges(paymentAudience);

  const handleRequestCancellation = async () => {
    // Pre-flight: fetch the FA-side workflow so the confirmation copy is
    // truthful. Non-zero wallet balance ⇒ convert to Guest (balance saved);
    // zero balance ⇒ full deactivation. Falls back to a generic message if
    // the preview call fails so the user is never blocked.
    let preview = null;
    try {
      const res = await axios.get(`${API}/members/cancellation-preview`, {
        headers: { Authorization: `Bearer ${walletToken}` },
        withCredentials: true,
      });
      preview = res.data;
    } catch { /* keep preview=null → generic copy */ }

    const bal = preview?.wallet_balance ?? 0;
    const willConvert = preview?.action === "convert_to_guest" || bal > 0;
    const message = willConvert
      ? `Request membership cancellation? Your wallet still has €${bal.toFixed(2)}. On Finance Admin approval your membership will end and your account will be converted back to a Guest — same email & password, wallet balance preserved. No annual fee will be refunded.`
      : "Request membership cancellation? On Finance Admin approval your account will be deactivated. No annual fee will be refunded for mid-year cancellations.";

    setConfirmDialog({
      title: willConvert ? "Cancel Membership (Convert to Guest)" : "Cancel Membership",
      message,
      onConfirm: async () => {
        setCancelLoading(true);
        try {
          await axios.post(`${API}/members/request-cancellation`, {}, {
            headers: { Authorization: `Bearer ${walletToken}` }
          });
          toast.success("Cancellation request submitted. Finance Admin will review it.");
          fetchDashboard();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Failed to submit request");
        } finally { setCancelLoading(false); }
      }
    });
  };

  const handleFamilyMemberDelink = async (fm) => {
    setConfirmDialog({
      title: "Remove Family Member",
      message: `Remove ${fm.name} from your family group? They will keep their own membership but will no longer share your family wallet.`,
      onConfirm: async () => {
        try {
          await axios.post(`${API}/wallet/family-member/${fm.member_id}/delink`, {}, {
            headers: { Authorization: `Bearer ${walletToken}` }
          });
          toast.success(`${fm.name} removed from family group`);
          fetchFamilyMembers();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Failed to remove family member");
        }
      }
    });
  };

  const handleFamilyMemberCancel = async (fm) => {
    setConfirmDialog({
      title: "Cancel Family Member",
      message: `Request membership cancellation for ${fm.name}? A Finance Admin will review this request.`,
      onConfirm: async () => {
        try {
          await axios.post(`${API}/wallet/family-member/${fm.member_id}/request-cancellation`, {}, {
            headers: { Authorization: `Bearer ${walletToken}` }
          });
          toast.success(`Cancellation request submitted for ${fm.name}`);
          fetchFamilyMembers();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Failed to submit cancellation request");
        }
      }
    });
  };

  const handleFamilyMemberDeactivate = async (fm) => {
    // Deactivation renamed to cancellation
    return handleFamilyMemberCancel(fm);
  };

  // Add family member after login
  const [showAddFamilyModal, setShowAddFamilyModal] = useState(false);
  const [addFmMode, setAddFmMode] = useState("new"); // "new" | "search"
  const [addFmForm, setAddFmForm] = useState({ name: "", email: "", dob: "", mobile_number: "", address: "" });
  const [addFmLoading, setAddFmLoading] = useState(false);
  const [addFmEmailVerified, setAddFmEmailVerified] = useState(false);
  const [addFmVerifCode, setAddFmVerifCode] = useState("");
  const [addFmVerifSent, setAddFmVerifSent] = useState(false);
  const [addFmVerifLoading, setAddFmVerifLoading] = useState(false);
  const [addFmPaymentPending, setAddFmPaymentPending] = useState(null); // { member_id, session_id }
  const [addFmPolling, setAddFmPolling] = useState(false);
  const [addFmStep, setAddFmStep] = useState("form"); // "form" | "agreements" | "payment" | "waiting"
  const [addFmSatzung, setAddFmSatzung] = useState(false);
  const [addFmGdpr, setAddFmGdpr] = useState(false);
  const [addFmPendingData, setAddFmPendingData] = useState(null);
  const [addFmWalletBalance, setAddFmWalletBalance] = useState(0);
  const [addFmShowSepa, setAddFmShowSepa] = useState(false);
  const [addFmSepaForm, setAddFmSepaForm] = useState({ account_holder: "", street: "", plz_city: "", bank_name: "", iban: "", bic: "" });
  const [addFmSepaPassword, setAddFmSepaPassword] = useState("");
  // iter142: guest-upgrade confirmations — Satzung + auto-renewal.
  // GDPR is intentionally omitted (already on file from guest registration).
  const [addFmUpgradeSatzung, setAddFmUpgradeSatzung] = useState(false);
  const [addFmUpgradeAutoSub, setAddFmUpgradeAutoSub] = useState(false);
  const [addFmSepaLoading, setAddFmSepaLoading] = useState(false); // holds response from add-family-member
  // Search & link state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkConsentCode, setLinkConsentCode] = useState("");
  const [linkCodeSent, setLinkCodeSent] = useState(false);
  const [linkVerifying, setLinkVerifying] = useState(false);

  const handleSearchMembers = async () => {
    if (searchQuery.length < 2) return;
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API}/wallet/search-members?q=${encodeURIComponent(searchQuery)}`, {
        headers: { Authorization: `Bearer ${walletToken}` }
      });
      setSearchResults(res.data || []);
      setHasSearched(true);
    } catch { toast.error("Search failed"); }
    finally { setSearchLoading(false); }
  };

  const handleSendLinkConsent = async (person) => {
    setLinkTarget(person);
    setLinkVerifying(true);
    try {
      await axios.post(`${API}/wallet/send-link-consent`, {
        person_id: person.person_id || person.member_id,
        kind: person.kind || "member",
      }, { headers: { Authorization: `Bearer ${walletToken}` } });
      setLinkCodeSent(true);
      toast.success(`Consent code sent to ${person.name}'s email`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send consent code");
    } finally { setLinkVerifying(false); }
  };

  const handleVerifyLinkConsent = async () => {
    if (!linkConsentCode) return toast.error("Enter the consent code");
    setLinkVerifying(true);
    try {
      const res = await axios.post(`${API}/wallet/verify-link-consent`, {
        person_id: linkTarget.person_id || linkTarget.member_id,
        kind: linkTarget.kind || "member",
        code: linkConsentCode,
      }, { headers: { Authorization: `Bearer ${walletToken}` } });

      // iter141: guest linking is now an UPGRADE — after consent we must
      // route through the same payment flow used for adding a new member.
      if (res.data?.kind === "guest") {
        const g = res.data.guest || {};
        // Pre-fill the payment step with the guest's data so the summary
        // shows the right name / fee.
        setAddFmForm(f => ({
          ...f,
          name: g.name || linkTarget.name || "",
          email: g.email || linkTarget.email || "",
          dob: g.dob || "",
          address: g.address || "",
          mobile_number: g.mobile_number || "",
          email_verified: true,   // already OTP-verified via consent
        }));
        // Kick the add-family-member request with `link_guest_id` — creates
        // the pending member row and returns `requires_payment: true`.
        const upgradeRes = await axios.post(`${API}/wallet/add-family-member`, {
          link_guest_id: g.guest_id,
        }, { headers: { Authorization: `Bearer ${walletToken}` } });
        setAddFmPendingData(upgradeRes.data);
        setLinkCodeSent(false);
        setLinkConsentCode("");
        // iter141b: the modal top-level branches on `addFmMode` ("new" | "link").
        // The linker branch was hiding the payment step even though addFmStep
        // was already "payment" — switch to "new" so the same payment surface
        // used for a fresh family member renders.
        setAddFmMode("new");
        // Fetch the requester's wallet balance so the payment step can
        // offer wallet-partial-pay.
        try {
          const wRes = await axios.get(`${API}/wallet/dashboard`, { headers: { Authorization: `Bearer ${walletToken}` } });
          setAddFmWalletBalance(wRes.data.wallet?.total_balance || 0);
        } catch { setAddFmWalletBalance(0); }
        setAddFmStep("payment");
        toast.success(`${g.name} consented. Complete payment to finalise the upgrade.`);
        return;
      }

      // Member linking — instant family_id write, done.
      toast.success(`${linkTarget.name} linked to your family!`);
      setShowAddFamilyModal(false);
      resetAddFmState();
      fetchFamilyMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally { setLinkVerifying(false); }
  };

  const handleAddFmValidate = () => {
    if (!addFmForm.name || !addFmForm.dob) return toast.error("Name and date of birth are required");
    const age = addFmForm.dob ? Math.floor((new Date() - new Date(addFmForm.dob)) / 31557600000) : 0;
    if (age >= 17 && !addFmForm.email) return toast.error("Email is required for adult family members");
    if (addFmForm.email && !addFmEmailVerified) return toast.error("Please verify the email address first");
    setAddFmStep("agreements");
  };

  const handleAddFmSubmitAndPay = async (useWallet = false) => {
    if (!addFmPendingData) {
      // First call: create member record
      if (!addFmSatzung || !addFmGdpr) return toast.error("Please agree to all terms");
      setAddFmLoading(true);
      try {
        const res = await axios.post(`${API}/wallet/add-family-member`, {
          ...addFmForm,
          email_verified: addFmEmailVerified,
        }, { headers: { Authorization: `Bearer ${walletToken}` } });
        setAddFmPendingData(res.data);

        if (!res.data.requires_payment) {
          toast.success(`${addFmForm.name} submitted for FA approval`);
          setShowAddFamilyModal(false);
          resetAddFmState();
          fetchFamilyMembers();
          return;
        }
        // Move to payment summary
        setAddFmStep("payment");
        // Fetch payer's wallet balance
        try {
          const wRes = await axios.get(`${API}/wallet/dashboard`, { headers: { Authorization: `Bearer ${walletToken}` } });
          setAddFmWalletBalance(wRes.data.wallet?.total_balance || 0);
        } catch { setAddFmWalletBalance(0); }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to add family member");
      } finally { setAddFmLoading(false); }
    } else {
      // Second call: process payment
      const memberId = addFmPendingData.member_id;
      setAddFmLoading(true);
      try {
        const payRes = await axios.post(`${API}/wallet/family-member/${memberId}/create-checkout`, {
          origin_url: window.location.origin + "/" + _slug(),
          use_wallet: useWallet,
        }, { headers: { Authorization: `Bearer ${walletToken}` } });

        if (payRes.data.no_payment_required) {
          toast.success(`${addFmForm.name} submitted for FA approval`);
          setShowAddFamilyModal(false);
          resetAddFmState();
          fetchFamilyMembers();
        } else {
          setAddFmPaymentPending({ member_id: memberId, session_id: payRes.data.session_id });
          setAddFmStep("waiting");
          window.open(payRes.data.checkout_url, "_blank");
          toast.info("Complete payment in the new tab.");
          pollFmPayment(payRes.data.session_id);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Payment setup failed");
      } finally { setAddFmLoading(false); }
    }
  };

  const pollFmPayment = async (sessionId) => {
    setAddFmPolling(true);
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setAddFmPolling(false);
        toast.error("Payment check timed out. Check your Stripe dashboard.");
        return;
      }
      try {
        const res = await axios.get(`${API}/wallet/family-member/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${walletToken}` }
        });
        if (res.data.status === "complete" && res.data.payment_status === "paid") {
          setAddFmPolling(false);
          setAddFmPaymentPending(null);
          toast.success("Payment successful! Member submitted for FA approval.");
          setShowAddFamilyModal(false);
          resetAddFmState();
          fetchFamilyMembers();
          return;
        }
        if (res.data.status === "expired") {
          setAddFmPolling(false);
          toast.error("Payment session expired.");
          return;
        }
      } catch (err) { console.error("Request failed:", err?.message || err); }
      attempts++;
      setTimeout(poll, 5000);
    };
    poll();
  };

  const handleAddFmSepaPayment = async () => {
    if (!addFmSepaForm.account_holder || !addFmSepaForm.street || !addFmSepaForm.plz_city || !addFmSepaForm.bank_name || !addFmSepaForm.iban) {
      return toast.error("Please fill in all required bank details");
    }
    if (!addFmPendingData) return;
    // iter142: guest-upgrade path also requires Satzung + auto-renewal
    // confirmations before submitting SEPA.
    if (addFmPendingData?.upgraded_from_guest_id) {
      if (!addFmUpgradeSatzung || !addFmUpgradeAutoSub) {
        return toast.error("Please confirm the Satzung and auto-renewal agreements");
      }
    }
    setAddFmSepaLoading(true);
    try {
      // iter142: wallet JWT is the electronic signature — no password needed.
      await axios.post(`${API}/wallet/sepa-mandate`, {
        member_id: addFmPendingData.member_id,
        payer_id: walletUser.customer_id,
        signer_email: walletUser.email || data?.customer?.email || "",
        ...addFmSepaForm,
      }, { headers: { Authorization: `Bearer ${walletToken}` } });
      toast.success("SEPA mandate submitted. Pending FA approval.");
      setShowAddFamilyModal(false);
      resetAddFmState();
      fetchFamilyMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SEPA submission failed");
    } finally { setAddFmSepaLoading(false); }
  };


  const handleSendFmVerification = async () => {
    if (!addFmForm.email) return toast.error("Enter email first");
    setAddFmVerifLoading(true);
    try {
      await axios.post(`${API}/wallet/family-member/send-verification`, {
        email: addFmForm.email,
        name: addFmForm.name,
      }, { headers: { Authorization: `Bearer ${walletToken}` } });
      setAddFmVerifSent(true);
      toast.success("Verification code sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send code");
    } finally { setAddFmVerifLoading(false); }
  };

  const handleVerifyFmEmail = async () => {
    if (!addFmVerifCode) return toast.error("Enter the verification code");
    setAddFmVerifLoading(true);
    try {
      await axios.post(`${API}/wallet/family-member/verify-email`, {
        email: addFmForm.email,
        code: addFmVerifCode,
      }, { headers: { Authorization: `Bearer ${walletToken}` } });
      setAddFmEmailVerified(true);
      toast.success("Email verified!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally { setAddFmVerifLoading(false); }
  };

  const resetAddFmState = () => {
    setAddFmForm({ name: "", email: "", dob: "", mobile_number: "", address: "" });
    setAddFmMode("new");
    setAddFmStep("form");
    setAddFmSatzung(false);
    setAddFmGdpr(false);
    setAddFmPendingData(null);
    setAddFmWalletBalance(0);
    setAddFmShowSepa(false);
    setAddFmSepaForm({ account_holder: "", street: "", plz_city: "", bank_name: "", iban: "", bic: "" });
    setAddFmSepaPassword("");
    setAddFmUpgradeSatzung(false);
    setAddFmUpgradeAutoSub(false);
    setAddFmEmailVerified(false);
    setAddFmVerifCode("");
    setAddFmVerifSent(false);
    setAddFmPaymentPending(null);
    setAddFmPolling(false);
    setSearchQuery("");
    setSearchResults([]);
    setLinkTarget(null);
    setLinkConsentCode("");
    setLinkCodeSent(false);
  };

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(null); // null | "self" | member_id
  const [editAddress, setEditAddress] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);

  const startEditProfile = (who, currentAddress, currentMobile) => {
    setEditingProfile(who);
    setEditAddress(currentAddress || "");
    setEditMobile(currentMobile || "");
  };

  const handleResumeFmDraft = async (fm) => {
    // iter144: re-open the Stripe setup checkout for an incomplete draft.
    // Same payload as the second click of handleAddFmSubmitAndPay, but
    // driven off the family list card so the payer can recover any time.
    setAddFmLoading(true);
    try {
      const payRes = await axios.post(`${API}/wallet/family-member/${fm.member_id}/create-checkout`, {
        origin_url: window.location.origin + "/" + _slug(),
        use_wallet: false,
      }, { headers: { Authorization: `Bearer ${walletToken}` } });
      if (payRes.data.no_payment_required) {
        toast.success(`${fm.name} submitted for FA approval`);
        fetchFamilyMembers();
      } else {
        window.open(payRes.data.checkout_url, "_blank");
        toast.info("Complete payment in the new tab.");
        pollFmPayment(payRes.data.session_id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resume payment");
    } finally { setAddFmLoading(false); }
  };

  const handleCancelFmDraft = async (fm) => {
    setConfirmDialog({
      title: "Cancel draft?",
      body: `${fm.name} has not completed payment. Cancelling deletes this draft. You can add them again anytime.`,
      onConfirm: async () => {
        try {
          await axios.post(`${API}/wallet/family-member/${fm.member_id}/cancel-draft`, {},
            { headers: { Authorization: `Bearer ${walletToken}` } });
          toast.success("Draft cancelled");
          fetchFamilyMembers();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Cancel failed");
        }
      },
    });
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const url = editingProfile === "self"
        ? `${API}/wallet/profile`
        : `${API}/wallet/family-member/${editingProfile}/profile`;
      const method = editingProfile === "self" ? "put" : "put";
      await axios({
        method,
        url,
        data: { address: editAddress, mobile_number: editMobile },
        headers: { Authorization: `Bearer ${walletToken}` }
      });
      toast.success("Profile updated");
      setEditingProfile(null);
      fetchDashboard();
      if (editingProfile !== "self") fetchFamilyMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update profile");
    } finally {
      setProfileSaving(false);
    }
  };

  const selectedAmount = topupAmount || (customAmount ? parseFloat(customAmount) : 0);
  const topupFee = selectedAmount > 0 ? calcFee(selectedAmount) : 0;
  const topupTotal = Math.round((selectedAmount + topupFee) * 100) / 100;

  const handleTopup = async () => {
    const amt = topupAmount || parseFloat(customAmount);
    if (!amt || amt < 1) { toast.error("Enter a valid top-up amount"); return; }
    setTopupLoading(true);
    try {
      const res = await axios.post(`${API}/wallet/stripe/create-checkout`, {
        package_id: topupAmount ? String(topupAmount) : null,
        custom_amount: topupAmount ? null : amt,
        origin_url: window.location.origin + "/" + _slug()
      }, { headers: { Authorization: `Bearer ${walletToken}` } });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setTopupLoading(false);
    }
  };

  const pollCheckoutStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 30;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/wallet/stripe/checkout-status/${sessionId}`, {
          headers: { Authorization: `Bearer ${walletToken}` }
        });
        if (res.data.payment_status === "paid") {
          setTopupPolling(false);
          toast.success(`€${res.data.topup_amount?.toFixed(2)} added to your wallet!`);
          setTab("wallet");
          fetchDashboard();
          return;
        }
        if (res.data.status === "expired") {
          setTopupPolling(false);
          toast.error("Payment session expired");
          return;
        }
      } catch { /* ignore polling errors */ }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setTopupPolling(false);
        toast.error("Payment verification timed out. Check your transactions.");
        fetchDashboard();
      }
    };
    poll();
  };

  const handleChangePassword = async () => {
    if (!currentPw || !newPw) { toast.error("Fill in all fields"); return; }
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmNewPw) { toast.error("New passwords don't match"); return; }
    setChangePwLoading(true);
    try {
      await axios.post(`${API}/wallet/change-password`, 
        { current_password: currentPw, new_password: newPw },
        { headers: { Authorization: `Bearer ${walletToken}` } }
      );
      toast.success("Password changed! Please login again.");
      setCurrentPw(""); setNewPw(""); setConfirmNewPw(""); setShowChangePassword(false);
      handleLogout();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally { setChangePwLoading(false); }
  };

  const handleDownloadCard = async () => {
    if (!data?.customer) return;
    const isMember = data.is_member;
    const cardLabel = isMember ? "MEMBER" : "GUEST";
    const w = 1080, h = 1350;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    // iter174: `CanvasRenderingContext2D.roundRect` was only shipped in
    // iOS Safari 16, Chrome 99, Firefox 111 — older mobile builds throw
    // "roundRect is not a function" and the whole download aborts. Fall
    // back to a manual arc path when the native method is missing.
    const _roundRect = (rx, ry, rw, rh, r) => {
      if (typeof ctx.roundRect === "function") { ctx.roundRect(rx, ry, rw, rh, r); return; }
      const rr = Math.min(r, rw / 2, rh / 2);
      ctx.moveTo(rx + rr, ry);
      ctx.arcTo(rx + rw, ry, rx + rw, ry + rh, rr);
      ctx.arcTo(rx + rw, ry + rh, rx, ry + rh, rr);
      ctx.arcTo(rx, ry + rh, rx, ry, rr);
      ctx.arcTo(rx, ry, rx + rw, ry, rr);
      ctx.closePath();
    };

    // Same-origin assets (logo, inline SVG) never need CORS. Setting
    // crossOrigin on data-URI images taints the canvas in Safari and
    // makes `toBlob` return null (silent fail on download).
    const loadImage = (src, useCors = false) => new Promise((resolve, reject) => {
      const img = new Image();
      if (useCors) img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("load failed"));
      img.src = src;
    });

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, "#1e3a5f");
    grad.addColorStop(1, "#2d5a8f");
    ctx.fillStyle = grad;
    ctx.beginPath();
    _roundRect(0, 0, w, h, 64);
    ctx.fill();

    // Logo top-right (large)
    const logoSize = 140;
    try {
      const logoImg = await loadImage("/mymaitree-logo-light.png");
      ctx.fillStyle = "rgba(255,255,255,1)";
      ctx.beginPath();
      _roundRect(w - logoSize - 80, 55, logoSize + 24, logoSize + 24, 28);
      ctx.fill();
      ctx.drawImage(logoImg, w - logoSize - 68, 67, logoSize, logoSize);
    } catch {
      // skip logo
    }

    // Card label
    ctx.fillStyle = "rgba(200,215,235,1)";
    ctx.font = "bold 42px sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(cardLabel, 80, 110);

    // Name
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 72px sans-serif";
    ctx.fillText(data.customer?.name || "", 80, 205);

    // Email
    ctx.fillStyle = "rgba(200,215,235,1)";
    ctx.font = "40px sans-serif";
    ctx.fillText(data.customer?.email || "", 80, 265);

    // QR white background
    const qrBgSize = 560;
    const qrX = (w - qrBgSize) / 2;
    const qrY = 350;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    _roundRect(qrX, qrY, qrBgSize, qrBgSize, 36);
    ctx.fill();

    // Render QR from SVG
    const qrEl = document.getElementById("wallet-qr-svg");
    if (qrEl) {
      try {
        const svgClone = qrEl.cloneNode(true);
        svgClone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        svgClone.setAttribute("width", "480");
        svgClone.setAttribute("height", "480");
        const svgData = new XMLSerializer().serializeToString(svgClone);
        const svgBase64 = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
        const qrImg = await loadImage(svgBase64);
        ctx.drawImage(qrImg, qrX + 40, qrY + 40, qrBgSize - 80, qrBgSize - 80);
      } catch {
        // QR render failed
      }
    }

    // Customer/Member ID
    const idY = qrY + qrBgSize + 60;
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 72px monospace";
    ctx.textAlign = "center";
    ctx.fillText(data.customer?.customer_id || data.customer?.member_id || "", w / 2, idY + 50);

    // Tagline
    ctx.fillStyle = "rgba(180,200,225,1)";
    ctx.font = "34px sans-serif";
    ctx.fillText("Your Space. Your Experience. Your Maitree.", w / 2, idY + 150);

    // Watermark
    ctx.fillStyle = "rgba(150,175,205,0.5)";
    ctx.font = "28px sans-serif";
    ctx.textAlign = "right";
    const watermark = "my.maitree-ev.org";
    ctx.fillText(watermark, w - 50, h - 45);

    // iter174: download via Blob + Object URL. `toDataURL` produced a
    // ~2 MB base64 string that some mobile browsers refused to trigger
    // an `<a download>` on. Blob URLs are lightweight, work in every
    // modern mobile browser, and iOS Safari (which still ignores the
    // `download` attribute) can be rescued by opening the blob in a new
    // tab where the user can long-press → Save Image.
    const prefix = isMember ? "mymaitree-member" : "mymaitree-guest";
    const cardId = data.customer?.customer_id || data.customer?.member_id || "card";
    const filename = `${prefix}-${cardId}.png`;
    const supportsDownload = "download" in document.createElement("a");
    // iOS Safari ships `download` on <a> but silently ignores it in
    // many versions. Detect it and route to a new-tab preview instead.
    const ua = (navigator.userAgent || "").toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua) || (ua.includes("mac") && "ontouchend" in document);
    const isIOSSafari = isIOS && /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);

    const trigger = (blobOrDataUrl) => {
      const isBlob = typeof blobOrDataUrl !== "string";
      const url = isBlob ? URL.createObjectURL(blobOrDataUrl) : blobOrDataUrl;
      if (isIOSSafari) {
        // Open in a new tab so user can save via long-press. iOS Safari
        // ignores the `download` attribute so we can't force the save
        // dialog — the tab preview is the closest we can get.
        const win = window.open(url, "_blank");
        if (!win) toast.info("Allow pop-ups to download the card, or long-press the image to save.");
        else toast.success("Long-press the image to save it to your device.");
      } else if (supportsDownload) {
        const link = document.createElement("a");
        link.download = filename;
        link.href = url;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`${isMember ? "Member" : "Wallet"} card downloaded!`);
      } else {
        // Legacy fallback — open in new tab.
        window.open(url, "_blank");
        toast.info("Right-click / long-press the image to save.");
      }
      // Free the object URL after the browser has had a chance to grab it.
      if (isBlob) setTimeout(() => URL.revokeObjectURL(url), 30000);
    };

    try {
      // Prefer Blob (mobile-friendly). Some very old browsers (Safari 10-)
      // lack toBlob — fall through to toDataURL there.
      if (typeof canvas.toBlob === "function") {
        canvas.toBlob((blob) => {
          if (!blob) {
            // toBlob returned null (rare) — fall back to dataURL.
            try { trigger(canvas.toDataURL("image/png")); }
            catch (e) { toast.error("Could not generate card image on this browser."); }
            return;
          }
          trigger(blob);
        }, "image/png");
      } else {
        trigger(canvas.toDataURL("image/png"));
      }
    } catch (e) {
      // Canvas taint or memory issues surface here.
      toast.error("Could not generate card image on this browser.");
    }
  };

  const filteredTransactions = data?.transactions?.filter(t => {
    if (!txSearch) return true;
    const q = txSearch.toLowerCase();
    return (
      t.type?.toLowerCase().includes(q) ||
      t.event_name?.toLowerCase().includes(q) ||
      t.amount?.toString().includes(q) ||
      t.payment_type?.toLowerCase().includes(q) ||
      new Date(t.created_at).toLocaleDateString().includes(q)
    );
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  // Empty-data safety net. Reached when fetchDashboard finishes without
  // populating `data` (5xx, network error, or unhandled shape). We show a
  // recoverable error screen with a Sign-out button — otherwise the user
  // gets a blank white page with no way out (esp. on mobile where dev
  // tools aren't at hand and clearing localStorage isn't obvious).
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-6" data-testid="dashboard-error-fallback">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-sm p-6 text-center">
          <p className="text-lg font-bold text-[#1e3a5f] mb-1">Couldn&apos;t load your dashboard</p>
          <p className="text-sm text-slate-500 mb-5">This can happen after an update or when your session has expired. Try signing out and back in.</p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { fetchDashboard(); }}
              className="w-full px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold"
              data-testid="dashboard-error-retry"
            >
              Try again
            </button>
            <button
              onClick={() => { authLogout(); navigate("/" + _slug() + "/login"); }}
              className="w-full px-4 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-sm font-semibold"
              data-testid="dashboard-error-signout"
            >
              Sign out & sign in again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Change password view
  if (showChangePassword) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100" data-testid="change-password-view">
        <div className="bg-[#1e3a5f] text-white p-4">
          <div className="max-w-md mx-auto flex items-center gap-3">
            {!forceChangePassword && (
              <button onClick={() => setShowChangePassword(false)} className="p-2 rounded-full hover:bg-white/10"><ArrowLeft className="w-5 h-5" /></button>
            )}
            <p className="font-bold">Change Password</p>
          </div>
        </div>
        <div className="max-w-md mx-auto p-6">
          {forceChangePassword && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 text-center" data-testid="force-change-pw-banner">
              <Lock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <p className="text-amber-800 font-medium text-sm">Your password was reset by an administrator.</p>
              <p className="text-amber-600 text-xs mt-1">Please set a new password to continue using your wallet.</p>
            </div>
          )}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="space-y-4">
              <PasswordInput value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder={forceChangePassword ? "Temporary Password" : "Current Password"} testId="current-pw" />
              <PasswordInput value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="New Password (min 8 chars)" testId="new-pw" />
              <PasswordInput value={confirmNewPw} onChange={e => setConfirmNewPw(e.target.value)} placeholder="Confirm New Password" testId="confirm-new-pw" />
              <button onClick={handleChangePassword} disabled={changePwLoading} className="w-full py-3 rounded-xl bg-[#1e3a5f] text-white font-bold disabled:opacity-50" data-testid="save-password-btn">
                {changePwLoading ? "Saving..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100" data-testid="wallet-dashboard">
      {/* Header with Logo */}
      <div className="bg-[#1e3a5f] text-white p-3 sm:p-4 sticky top-0 z-10">
        <div className="max-w-md mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/mymaitree-logo-light.png" alt="My Maitree" className="w-9 h-9 rounded-lg shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
            <div className="min-w-0">
              <p className="text-white/70 text-[10px] truncate">{data?.is_member ? "My Maitree Member" : "My Maitree Guest"}</p>
              <p className="font-bold text-sm truncate">{data?.customer?.name || walletUser.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {hasModule && hasModule("ticketing") && (
              <button onClick={() => navigate("/" + _slug() + "/")} className="px-2 sm:px-3 py-1.5 rounded-full bg-[#2d8a4e] text-white text-xs font-bold flex items-center gap-1 hover:bg-[#236939]" data-testid="buy-tickets-cta" title="Buy Tickets">
                <Ticket className="w-4 h-4" /> <span className="hidden sm:inline">Buy Tickets</span>
              </button>
            )}
            {hasModule && hasModule("ticketing") && cart && (
              <button onClick={() => cart.open()} className="relative p-1.5 rounded-full hover:bg-white/10 text-white/90" data-testid="header-cart-btn" title="Cart">
                <ShoppingCart className="w-5 h-5" />
                {cart.count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="header-cart-count">{cart.count}</span>
                )}
              </button>
            )}
            <button onClick={() => setShowChangePassword(true)} className="p-1.5 rounded-full hover:bg-white/10 text-white/70" data-testid="change-pw-btn" title="Change Password">
              <Key className="w-5 h-5" />
            </button>
            <button onClick={() => navigate(`/${_slug()}/help?role=${data?.is_member ? "member" : "guest"}`)} className="p-1.5 rounded-full hover:bg-white/10 text-white/70" title="Help">
              <HelpCircle className="w-5 h-5" />
            </button>
            <button onClick={handleLogout} className="p-1.5 rounded-full hover:bg-white/10 text-white/70" data-testid="logout-btn" title="Logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Tab bar — labels hidden on <sm to prevent 5-tab overlap on phones.
            iter175: widened container + per-button padding + slightly larger
            gap on desktop so the labels don't butt up against their icons
            (e.g. "TransactionsO Membership" running together on wide screens). */}
        <div className="max-w-md sm:max-w-2xl mx-auto flex mt-3 border-t border-white/10 sm:gap-1">
          <button onClick={() => setTab("wallet")} className={`flex-1 py-2 sm:py-3 px-1 sm:px-2 flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${tab === "wallet" ? "border-white text-white" : "border-transparent text-white/50"}`} data-testid="tab-wallet">
            <Wallet className="w-4 h-4" /> <span className="hidden sm:inline">Wallet</span>
          </button>
          <button onClick={() => setTab("topup")} className={`flex-1 py-2 sm:py-3 px-1 sm:px-2 flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${tab === "topup" ? "border-white text-white" : "border-transparent text-white/50"}`} data-testid="tab-topup">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Top Up</span>
          </button>
          <button onClick={() => setTab("transactions")} className={`flex-1 py-2 sm:py-3 px-1 sm:px-2 flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${tab === "transactions" ? "border-white text-white" : "border-transparent text-white/50"}`} data-testid="tab-transactions">
            <History className="w-4 h-4" /> <span className="hidden sm:inline">Transactions</span>
          </button>
          {data.is_member && (
            <button onClick={() => setTab("membership")} className={`flex-1 py-2 sm:py-3 px-1 sm:px-2 flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${tab === "membership" ? "border-white text-white" : "border-transparent text-white/50"}`} data-testid="tab-membership">
              <BadgeCheck className="w-4 h-4" /> <span className="hidden sm:inline">Membership</span>
            </button>
          )}
          {hasModule && hasModule("ticketing") && (
            <button onClick={() => setTab("tickets")} className={`flex-1 py-2 sm:py-3 px-1 sm:px-2 flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${tab === "tickets" ? "border-white text-white" : "border-transparent text-white/50"}`} data-testid="tab-tickets">
              <Ticket className="w-4 h-4" /> <span className="hidden sm:inline">Tickets</span>
            </button>
          )}
          {data.is_member && (
            <button onClick={() => setTab("expenses")} className={`flex-1 py-2 sm:py-3 px-1 sm:px-2 flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${tab === "expenses" ? "border-white text-white" : "border-transparent text-white/50"}`} data-testid="tab-expenses">
              <Receipt className="w-4 h-4" /> <span className="hidden sm:inline">Expenses</span>
            </button>
          )}
        </div>
      </div>

      <div className="max-w-md mx-auto px-6 py-6">
        {/* Pending member approval banner — driven by the LIVE dashboard fetch
            only. `walletUser.pending_member_approval` is a login-time flag
            that stays stale until logout — after a mid-session convert-to-
            guest post-cancellation it lingers as `true` and used to leak the
            banner in the guest view. Dashboard is refetched on mount / focus
            / visibility, so `data.pending_member_approval` is authoritative
            and fresh. */}
        {!data.is_member && data.pending_member_approval && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4" data-testid="pending-approval-banner">
            <p className="text-amber-800 text-sm font-semibold">Membership Pending Approval</p>
            <p className="text-amber-600 text-xs mt-1">Your membership application is being reviewed by the Finance Admin. You&apos;ll receive an email once approved. In the meantime, you can continue using your guest wallet.</p>
          </div>
        )}

        {/* WALLET TAB */}
        {tab === "wallet" && (
          <>
            {/* QR Digital Card */}
            <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2d5a8f] rounded-3xl p-6 shadow-lg mb-6 relative" data-testid="digital-card">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-white/50 text-xs">{data.is_member ? "MEMBER" : "GUEST"}</p>
                  <p className="text-white font-bold text-lg">{data.customer?.name}</p>
                  <p className="text-white/60 text-xs">{data.customer?.email}</p>
                </div>
                <img src="/mymaitree-logo-light.png" alt="My Maitree" className="w-14 h-14 rounded-xl" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <div className="bg-white rounded-2xl p-3 flex items-center justify-center mb-4 mx-auto w-fit">
                <QRCode id="wallet-qr-svg" value={data.customer?.customer_id || data.customer?.member_id || ""} size={128} />
              </div>
              <div className="flex justify-between items-center">
                <p className="text-white font-mono text-lg tracking-wider">{data.customer?.customer_id || data.customer?.member_id}</p>
                <button onClick={handleDownloadCard} className="p-2 rounded-full bg-white/10 hover:bg-white/20" title={`Download ${data.is_member ? "Member" : "Wallet"} Card`} data-testid="download-card-btn">
                  <Download className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Balance Card */}
            <div className="bg-white rounded-2xl p-5 shadow-sm mb-4">
              <div className="flex justify-between items-center mb-3">
                <p className="text-slate-500 text-sm">Total Balance</p>
                <button onClick={fetchDashboard} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="refresh-balance">
                  <RefreshCw className="w-4 h-4 text-slate-400" />
                </button>
              </div>
              <p className="text-3xl font-bold text-[#1e3a5f] mb-4">€{data.wallet?.total_balance?.toFixed(2) || "0.00"}</p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-xl p-3">
                  <CreditCard className="w-4 h-4 text-green-500 mb-1" />
                  <p className="text-green-600 font-bold">€{data.wallet?.perpetual_balance?.toFixed(2) || "0.00"}</p>
                  <p className="text-slate-500 text-xs">No Expiry</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3">
                  <TrendingDown className="w-4 h-4 text-amber-500 mb-1" />
                  <p className="text-amber-600 font-bold">€{data.wallet?.expiring_balance?.toFixed(2) || "0.00"}</p>
                  <p className="text-slate-500 text-xs">Non-Refundable</p>
                </div>
              </div>

              {/* Expiring details - show only soonest expiry, summed by date */}
              {data.wallet?.expiring_details && data.wallet.expiring_details.length > 0 && (() => {
                // Group by date and sum amounts
                const grouped = {};
                data.wallet.expiring_details.forEach(eb => {
                  const dateKey = new Date(eb.expiry_date).toLocaleDateString();
                  grouped[dateKey] = (grouped[dateKey] || 0) + eb.amount;
                });
                // Find soonest date
                const soonest = data.wallet.expiring_details
                  .map(eb => new Date(eb.expiry_date))
                  .sort((a, b) => a - b)[0];
                const soonestKey = soonest.toLocaleDateString();
                return (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex justify-between items-center text-sm" data-testid="soonest-expiry">
                      <span className="text-slate-600">Expiring On: {soonestKey}</span>
                      <span className="font-bold text-amber-600">€{grouped[soonestKey].toFixed(2)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Become a Member CTA — hidden if the user has any pending member application. Drives off the live dashboard only; login-time flag is stale after convert-to-guest. */}
            {!data.is_member && !data.pending_member_approval && hasModule("memberships") && (
              <div className="bg-gradient-to-r from-[#1e3a5f]/5 to-purple-50 border border-[#1e3a5f]/20 rounded-2xl p-5 mt-2" data-testid="become-member-card">
                <div className="flex items-start gap-3 mb-3">
                  <Heart className="w-5 h-5 text-[#1e3a5f] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-[#1e3a5f] text-sm">Become a My Maitree Member</p>
                    <p className="text-slate-500 text-xs mt-0.5">Join as a full member — your wallet balance stays unchanged.</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/${_slug()}/member-register?email=${encodeURIComponent(data.customer?.email || "")}`)}
                  className="w-full py-2.5 rounded-xl bg-[#1e3a5f] text-white font-semibold text-sm"
                  data-testid="become-member-btn">
                  Join as a Member
                </button>
              </div>
            )}

            {/* Membership pending notification — replaces Join CTA */}
            {!data.is_member && data.pending_member_approval && (
              <div className="hidden" data-testid="membership-pending-card"></div>
            )}
          </>
        )}
        {tab === "topup" && (
          <>
            {topupPolling ? (
              <div className="bg-white rounded-2xl p-8 text-center" data-testid="topup-polling">
                <Loader2 className="w-10 h-10 text-[#1e3a5f] mx-auto mb-3 animate-spin" />
                <p className="text-slate-700 font-medium">Verifying your payment...</p>
                <p className="text-slate-400 text-sm mt-1">This may take a few seconds</p>
              </div>
            ) : (
              <>
                <p className="text-slate-500 text-sm mb-4">Add funds to your wallet via card, Apple Pay, Google Pay, or PayPal.</p>
                {/* Preset Packages */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {PACKAGES.map(pkg => (
                    <button
                      key={pkg.id}
                      onClick={() => { setTopupAmount(pkg.amount); setCustomAmount(""); }}
                      className={`p-4 rounded-2xl border-2 text-center transition-all ${topupAmount === pkg.amount ? "border-[#1e3a5f] bg-[#1e3a5f]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
                      data-testid={`topup-pkg-${pkg.id}`}
                    >
                      <p className="text-2xl font-bold text-[#1e3a5f]">€{pkg.amount}</p>
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
                  <p className="text-slate-500 text-xs mb-2">Or enter a custom amount (min €1)</p>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    value={customAmount}
                    onChange={e => { setCustomAmount(e.target.value); setTopupAmount(null); }}
                    placeholder="Custom amount"
                    className="w-full p-3 rounded-xl border bg-white text-lg"
                    data-testid="topup-custom-input"
                  />
                </div>

                {/* Fee Breakdown */}
                {selectedAmount > 0 && (
                  <div className="bg-slate-50 rounded-2xl p-4 mb-4" data-testid="topup-fee-breakdown">
                    <div className="flex justify-between text-sm text-slate-600 mb-1">
                      <span>Wallet Top-up</span>
                      <span>€{selectedAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-400 mb-1">
                      <span>Processing Fee ({chargesLabel})</span>
                      <span>€{topupFee.toFixed(2)}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 flex items-center gap-1 mb-2" data-testid="topup-fee-nonrefundable-note">
                      <Info className="w-3 h-3" /> Note: Processing fees are non-refundable
                    </p>
                    <div className="flex justify-between text-base font-bold text-[#1e3a5f] pt-2 border-t">
                      <span>Total</span>
                      <span>€{topupTotal.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Pay Button */}
                <button
                  onClick={handleTopup}
                  disabled={topupLoading || selectedAmount <= 0}
                  className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="topup-pay-btn"
                >
                  {topupLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</> : <><CreditCard className="w-5 h-5" /> Pay €{topupTotal > 0 ? topupTotal.toFixed(2) : "0.00"}</>}
                </button>
              </>
            )}
          </>
        )}

        {tab === "expenses" && data.is_member && <MemberExpenses token={walletToken} ownWalletId={data.customer?.member_id || data.customer?.customer_id || ""} />}

        {/* TICKETS TAB */}
        {tab === "tickets" && hasModule("ticketing") && (
          <MyTicketsList tenantSlug={_slug()} />
        )}

        {/* TRANSACTIONS HISTORY TAB */}
        {tab === "transactions" && (
          <>
            {/* Search */}
            <div className="mb-4">
              <input 
                value={txSearch}
                onChange={e => setTxSearch(e.target.value)}
                placeholder="Search by date, amount, event, type..."
                className="w-full p-3 rounded-xl border bg-white"
                data-testid="tx-search-input"
              />
            </div>

            {filteredTransactions.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-400">{txSearch ? "No matching transactions" : "No transactions yet"}</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {filteredTransactions.map((t, i) => {
                  const _isMembershipFee = (t.type === "membership_fee" || t.type === "membership_fees");
                  // Stripe-paid membership fees don't move wallet balance —
                  // they're card charges recorded on the unified ledger for
                  // audit. Render as a neutral, sign-less row so members
                  // don't misread them as wallet debits.
                  const _isStripeOnlyFee = _isMembershipFee && (t.payment_type === "stripe") && (!t.wallet_portion || t.wallet_portion === 0);
                  const _isCredit = (
                    t.type === "topup" ||
                    (typeof t.type === "string" && t.type.includes("refund")) ||
                    t.type === "on_attend_credit" ||
                    t.type === "expense_payout"
                  );
                  const _amountColor = _isStripeOnlyFee
                    ? "text-slate-600"
                    : (_isCredit ? "text-green-600" : "text-orange-500");
                  const _amountSign = _isStripeOnlyFee ? "" : (_isCredit ? "+" : "-");
                  return (
                  <div key={t.id || i} className={`p-4 ${i > 0 ? "border-t" : ""}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className={`font-bold ${_amountColor}`}>
                          {_amountSign}€{Math.abs(t.amount || 0).toFixed(2)}
                        </p>
                        <p className="text-slate-500 text-sm">
                          {(() => {
                            if (t.type === "topup") return `Top-up (${t.payment_type || "cash"})`;
                            // Any refund subtype (`refund`, `ticket_refund`,
                            // `ticket_cancellation_refund`, `membership_refund`) → single "Refund" label.
                            if (typeof t.type === "string" && t.type.includes("refund")) return "Refund";
                            if (t.type === "ticket_purchase") return "Ticket Purchase";
                            if (t.type === "merchant_sale") return "Merchant Purchase";
                            if (_isMembershipFee) return _isStripeOnlyFee ? "Membership Fee \u00b7 Paid via Stripe" : "Membership Fee";
                            if (t.type === "on_attend_credit") return "Coupon Credit";
                            if (t.type === "expense_payout") return "Expense Payout";
                            return "Purchase";
                          })()}
                          {t.event_name && <span className="text-slate-400"> {"\u2022"} {t.event_name}</span>}
                        </p>
                      </div>
                      <p className="text-slate-400 text-xs">{new Date(t.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                );})}
              </div>
            )}
          </>
        )}
        {/* MEMBERSHIP TAB (members only) */}
        {tab === "membership" && data.is_member && (
          <>
            <PayoutDetailsCard token={walletToken} />
            <div className="bg-white rounded-2xl p-5 shadow-sm mb-4">
              <h3 className="font-bold text-[#1e3a5f] mb-4">Membership Details</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-purple-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">Type</p>
                  <p className="font-bold text-purple-700">{data.customer?.member_type_name || "—"}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">Annual Fee</p>
                  <p className="font-bold text-green-600">€{(data.customer?.annual_fee || 0).toFixed(2)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">Member Since</p>
                  <p className="font-bold text-sm">{data.customer?.membership_start_date || (data.customer?.approved_at ? new Date(data.customer.approved_at).toLocaleDateString() : (data.customer?.created_at ? new Date(data.customer.created_at).toLocaleDateString() : "—"))}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">Member ID</p>
                  <p className="font-bold font-mono text-xs">{data.customer?.member_id || data.customer?.customer_id || "—"}</p>
                </div>
                <div className={`rounded-xl p-3 ${data.customer?.last_membership_fee_paid_date ? "bg-green-50" : "bg-amber-50"}`}>
                  <p className="text-xs text-slate-500">Last Fee Paid</p>
                  <p className={`font-bold text-sm ${data.customer?.last_membership_fee_paid_date ? "text-green-600" : "text-amber-600"}`}>
                    {data.customer?.last_membership_fee_paid_date || "Not recorded"}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">Mobile</p>
                  <p className="font-bold text-sm">{data.customer?.mobile_number || "—"}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 col-span-2">
                  <p className="text-xs text-slate-500">Address</p>
                  <p className="font-bold text-sm">{data.customer?.address || "—"}</p>
                </div>
              </div>
              {/* Edit profile button */}
              {editingProfile === "self" ? (
                <div className="mt-3 bg-slate-50 rounded-xl p-4 space-y-3">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block flex items-center gap-1"><Phone className="w-3 h-3" /> Mobile</label>
                    <input value={editMobile} onChange={e => setEditMobile(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="edit-self-mobile" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</label>
                    <textarea value={editAddress} onChange={e => setEditAddress(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border text-sm resize-none" data-testid="edit-self-address" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleSaveProfile} disabled={profileSaving} className="flex-1 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium disabled:opacity-50" data-testid="save-self-profile">
                      {profileSaving ? "Saving..." : "Save"}
                    </button>
                    <button onClick={() => setEditingProfile(null)} className="flex-1 py-2 rounded-lg bg-slate-200 text-sm font-medium">Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => startEditProfile("self", data.customer?.address, data.customer?.mobile_number)}
                  className="mt-3 w-full py-2 rounded-xl bg-slate-50 text-[#1e3a5f] text-sm font-medium flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors"
                  data-testid="edit-self-profile-btn"
                >
                  <Edit2 className="w-3.5 h-3.5" /> Edit Phone / Address
                </button>
              )}
            </div>

            {data.customer?.approval_status === "approved" && !data.customer?.cancellation_requested && (
              <button
                onClick={handleRequestCancellation}
                disabled={cancelLoading}
                className="w-full py-3 rounded-xl bg-red-50 text-red-600 font-bold border border-red-200 mb-4 disabled:opacity-50"
                data-testid="cancel-membership-btn"
              >
                {cancelLoading ? "Submitting..." : "Request Membership Cancellation"}
              </button>
            )}
            {data.customer?.cancellation_requested && (
              <div className="bg-amber-50 rounded-xl p-4 text-center mb-4" data-testid="cancel-requested-banner">
                <p className="text-amber-700 font-medium text-sm">Cancellation request pending Finance Admin review</p>
              </div>
            )}

            {data.subscription_transactions?.length > 0 && (
              <div>
                <h3 className="font-bold text-slate-600 mb-3 text-sm">Fee Payment History</h3>
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  {data.subscription_transactions.map((t, i) => (
                    <div key={t.id || i} className={`p-4 ${i > 0 ? "border-t" : ""}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-bold text-green-600">€{t.annual_fee?.toFixed(2)}</p>
                          <p className="text-slate-500 text-sm">Annual subscription fee</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-xs font-bold ${t.payment_status === "paid" ? "text-green-600" : "text-amber-600"}`}>
                            {t.payment_status?.toUpperCase()}
                          </p>
                          <p className="text-slate-400 text-xs">{new Date(t.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Family Members Section */}
            {data.is_member && (
              <div className="mt-4" data-testid="family-members-section">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold text-slate-600 text-sm flex items-center gap-2">
                    <Users className="w-4 h-4" /> Family Members
                  </h3>
                  <button
                    onClick={() => setShowAddFamilyModal(true)}
                    className="px-3 py-1.5 rounded-lg bg-[#1e3a5f] text-white text-xs font-medium flex items-center gap-1"
                    data-testid="add-family-member-btn"
                  >
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>

                {/* Add Family Member Modal */}
                {showAddFamilyModal && (
                  <div className="bg-slate-50 rounded-2xl p-4 mb-4 space-y-3" data-testid="add-family-modal">
                    <div className="flex gap-2 mb-2">
                      <button onClick={() => setAddFmMode("new")} className={`flex-1 py-2 rounded-lg text-xs font-medium ${addFmMode === "new" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600"}`}>
                        <Plus className="w-3 h-3 inline mr-1" /> Add New
                      </button>
                      <button onClick={() => setAddFmMode("search")} className={`flex-1 py-2 rounded-lg text-xs font-medium ${addFmMode === "search" ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600"}`}>
                        <Search className="w-3 h-3 inline mr-1" /> Link Existing
                      </button>
                    </div>
                    {addFmMode === "new" ? (
                      addFmStep === "form" ? (
                      <div className="space-y-2">
                        <input value={addFmForm.name} onChange={e => setAddFmForm({...addFmForm, name: e.target.value})} placeholder="Full name *" className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="add-fm-name" />
                        <div><label className="text-xs text-slate-500 mb-1 block">Date of birth *</label><input value={addFmForm.dob} onChange={e => setAddFmForm({...addFmForm, dob: e.target.value})} type="date" max={new Date().toISOString().slice(0,10)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="add-fm-dob" /></div>

                        {/* Email with inline verification */}
                        <div className="space-y-1">
                          <div className="flex gap-2">
                            <input
                              value={addFmForm.email || ""}
                              onChange={e => { setAddFmForm({...addFmForm, email: e.target.value}); setAddFmEmailVerified(false); setAddFmVerifSent(false); setAddFmVerifCode(""); }}
                              type="email"
                              placeholder="Email (required for adults)"
                              className="flex-1 px-3 py-2 rounded-lg border text-sm"
                              disabled={addFmEmailVerified}
                              data-testid="add-fm-email"
                            />
                            {addFmForm.email && !addFmEmailVerified && (
                              <button onClick={handleSendFmVerification} disabled={addFmVerifLoading} className="px-3 py-2 rounded-lg bg-amber-500 text-white text-xs font-medium disabled:opacity-50 whitespace-nowrap" data-testid="add-fm-send-code">
                                {addFmVerifLoading ? "..." : addFmVerifSent ? "Resend" : "Verify"}
                              </button>
                            )}
                            {addFmEmailVerified && <span className="px-3 py-2 text-green-600 text-xs font-medium flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Verified</span>}
                          </div>
                          {addFmVerifSent && !addFmEmailVerified && (
                            <div className="flex gap-2">
                              <input value={addFmVerifCode} onChange={e => setAddFmVerifCode(e.target.value)} placeholder="6-digit code" maxLength={6} className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono tracking-wider" data-testid="add-fm-verif-code" />
                              <button onClick={handleVerifyFmEmail} disabled={addFmVerifLoading} className="px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-medium disabled:opacity-50" data-testid="add-fm-verify-code">{addFmVerifLoading ? "..." : "Confirm"}</button>
                            </div>
                          )}
                        </div>

                        <input value={addFmForm.mobile_number || ""} onChange={e => setAddFmForm({...addFmForm, mobile_number: e.target.value})} type="tel" placeholder="Mobile (optional)" className="w-full px-3 py-2 rounded-lg border text-sm" />
                        <textarea value={addFmForm.address || ""} onChange={e => setAddFmForm({...addFmForm, address: e.target.value})} placeholder="Address (defaults to yours)" rows={2} className="w-full px-3 py-2 rounded-lg border text-sm resize-none" />
                        <div className="flex gap-2">
                          <button onClick={handleAddFmValidate} disabled={addFmLoading} className="flex-1 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium disabled:opacity-50" data-testid="add-fm-submit">Continue</button>
                          <button onClick={() => { setShowAddFamilyModal(false); resetAddFmState(); }} className="flex-1 py-2 rounded-lg bg-slate-200 text-sm font-medium">Cancel</button>
                        </div>
                      </div>
                      ) : addFmStep === "agreements" ? (
                      <div className="space-y-3">
                        <p className="text-sm font-bold text-[#1e3a5f]">Terms & Agreements for {addFmForm.name}</p>
                        <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer text-sm ${addFmSatzung ? "border-green-400 bg-green-50" : "border-slate-200"}`}>
                          <input type="checkbox" checked={addFmSatzung} onChange={e => setAddFmSatzung(e.target.checked)} className="mt-0.5 accent-green-500 w-4 h-4" data-testid="add-fm-satzung" />
                          <span className="text-slate-700">I confirm the new member agrees to the <a href="https://maitree-ev.org/satzung/" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline" onClick={e => e.stopPropagation()}>Satzung (Bylaws) of Maitree e.V.</a></span>
                        </label>
                        <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer text-sm ${addFmGdpr ? "border-green-400 bg-green-50" : "border-slate-200"}`}>
                          <input type="checkbox" checked={addFmGdpr} onChange={e => setAddFmGdpr(e.target.checked)} className="mt-0.5 accent-green-500 w-4 h-4" data-testid="add-fm-gdpr" />
                          <span className="text-slate-700">I consent to Maitree e.V. storing and processing this member's data (GDPR).</span>
                        </label>
                        <div className="flex gap-2">
                          <button onClick={() => handleAddFmSubmitAndPay()} disabled={!addFmSatzung || !addFmGdpr || addFmLoading} className="flex-1 py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium disabled:opacity-50" data-testid="add-fm-agree-pay">
                            {addFmLoading ? "Processing..." : "Agree & Continue"}
                          </button>
                          <button onClick={() => setAddFmStep("form")} className="flex-1 py-2 rounded-lg bg-slate-200 text-sm font-medium">Back</button>
                        </div>
                      </div>
                    ) : addFmStep === "payment" && addFmPendingData ? (
                      (() => {
                        const fee = addFmPendingData.annual_fee || 0;
                        // Reuse the same hook value that top-up uses so both
                        // preview lines match backend `calculate_processing_fee`.
                        const fullStripeFee = calcFee(fee);
                        const fullTotal = Math.round((fee + fullStripeFee) * 100) / 100;
                        // iter142: guest upgrade needs Satzung + auto-renewal
                        // confirmations here (GDPR already on file). New family
                        // members already collected these on the agreements step,
                        // so gate only when we're on the upgrade path.
                        const isGuestUpgrade = !!addFmPendingData.upgraded_from_guest_id;
                        const upgradeGateOk = !isGuestUpgrade || (addFmUpgradeSatzung && addFmUpgradeAutoSub);

                        return (
                      <div className="space-y-3">
                        <p className="text-sm font-bold text-[#1e3a5f]">Payment for {addFmForm.name}</p>
                        <div className="bg-[#1e3a5f] text-white rounded-xl p-4 text-center">
                          <p className="text-xs text-white/60">Annual Membership Fee</p>
                          <p className="text-2xl font-extrabold">{"\u20ac"}{fee.toFixed(2)}</p>
                        </div>

                        {/* iter142: Guest upgrade — Satzung + auto-renewal gates */}
                        {isGuestUpgrade && (
                          <div className="space-y-2" data-testid="add-fm-upgrade-agreements">
                            <p className="text-[11px] text-slate-500">
                              GDPR consent is already on file from {addFmForm.name}&apos;s guest registration. Please confirm the remaining agreements to proceed.
                            </p>
                            <label className={`flex items-start gap-2 p-2.5 rounded-lg border-2 cursor-pointer text-xs ${addFmUpgradeSatzung ? "border-green-400 bg-green-50" : "border-slate-200"}`}>
                              <input type="checkbox" checked={addFmUpgradeSatzung} onChange={e => setAddFmUpgradeSatzung(e.target.checked)} className="mt-0.5 accent-green-500 w-4 h-4" data-testid="add-fm-upgrade-satzung" />
                              <span className="text-slate-700">
                                I confirm {addFmForm.name} agrees to the <a href="https://maitree-ev.org/satzung/" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline" onClick={e => e.stopPropagation()}>Satzung (Bylaws) of Maitree e.V.</a>
                              </span>
                            </label>
                            <label className={`flex items-start gap-2 p-2.5 rounded-lg border-2 cursor-pointer text-xs ${addFmUpgradeAutoSub ? "border-green-400 bg-green-50" : "border-slate-200"}`}>
                              <input type="checkbox" checked={addFmUpgradeAutoSub} onChange={e => setAddFmUpgradeAutoSub(e.target.checked)} className="mt-0.5 accent-green-500 w-4 h-4" data-testid="add-fm-upgrade-auto-sub" />
                              <span className="text-slate-700">
                                I agree to the annual subscription — {"\u20ac"}{fee.toFixed(2)}/year, auto-renewing.
                              </span>
                            </label>
                          </div>
                        )}

                        {/* Stripe payment */}
                        <div>
                          <div className="bg-slate-50 rounded-xl p-3 mb-2 text-xs space-y-0.5">
                            <div className="flex justify-between"><span className="text-slate-500">Membership fee</span><span>{"\u20ac"}{fee.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">Processing fee ({chargesLabel})</span><span>{"\u20ac"}{fullStripeFee.toFixed(2)}</span></div>
                            <p className="text-[10px] text-slate-400 flex items-center gap-1" data-testid="add-fm-fee-nonrefundable-note">
                              <Info className="w-3 h-3" /> Note: Processing fees are non-refundable
                            </p>
                            <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span>{"\u20ac"}{fullTotal.toFixed(2)}</span></div>
                          </div>
                          <button onClick={() => handleAddFmSubmitAndPay(false)} disabled={addFmLoading || !upgradeGateOk} className="w-full py-2 rounded-lg bg-[#1e3a5f] text-white text-sm font-bold disabled:opacity-50" data-testid="add-fm-pay-stripe">
                            {addFmLoading ? "Processing..." : `Save Card & Submit \u2014 \u20ac${fullTotal.toFixed(2)}`}
                          </button>
                          <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-2.5 py-2" data-testid="add-fm-setup-first-reassurance">
                            <p className="text-[11px] text-emerald-800 font-semibold flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Nothing charged yet
                            </p>
                            <p className="text-[10px] text-emerald-700/90 mt-0.5 leading-snug">
                              We&apos;ll only bill {"\u20ac"}{fullTotal.toFixed(2)} once a Finance Admin approves this application. If rejected, nothing is charged. Card details are handled by Stripe &mdash; Maitree e.V. never sees or saves them.
                            </p>
                          </div>
                        </div>

                        {/* SEPA Direct Debit Option */}
                        <div className="border-t mt-3 pt-3">
                          <button onClick={() => setAddFmShowSepa(!addFmShowSepa)} className="w-full text-left flex items-center justify-between py-1 text-xs font-medium text-[#1e3a5f]">
                            <span>SEPA Direct Debit (no fees)</span>
                            <span className="text-slate-400">{addFmShowSepa ? "\u25B2" : "\u25BC"}</span>
                          </button>
                          {addFmShowSepa && (
                            <div className="bg-blue-50 rounded-xl p-3 mt-2 space-y-2 text-xs">
                              <div className="bg-white rounded-lg p-2 text-slate-600 space-y-0.5">
                                <p className="font-bold text-[#1e3a5f]">{"Zahlungsempf\u00e4nger"}</p>
                                <p>{"Maitree e.V., W\u00fcrttembergstr 46, 73733 Esslingen"}</p>
                                <p>{"CI: DE89ZZZ00002524344 \u00b7 Ref: Maitree e.V. Beitrag"}</p>
                              </div>
                              <input value={addFmSepaForm.account_holder} onChange={e => setAddFmSepaForm({...addFmSepaForm, account_holder: e.target.value})} placeholder="Kontoinhaber *" className="w-full px-2 py-1.5 rounded-lg border text-xs" />
                              <input value={addFmSepaForm.street} onChange={e => setAddFmSepaForm({...addFmSepaForm, street: e.target.value})} placeholder="Stra\u00dfe, Hausnummer *" className="w-full px-2 py-1.5 rounded-lg border text-xs" />
                              <input value={addFmSepaForm.plz_city} onChange={e => setAddFmSepaForm({...addFmSepaForm, plz_city: e.target.value})} placeholder="PLZ, Ort *" className="w-full px-2 py-1.5 rounded-lg border text-xs" />
                              <input value={addFmSepaForm.bank_name} onChange={e => setAddFmSepaForm({...addFmSepaForm, bank_name: e.target.value})} placeholder="Bank *" className="w-full px-2 py-1.5 rounded-lg border text-xs" />
                              <input value={addFmSepaForm.iban} onChange={e => setAddFmSepaForm({...addFmSepaForm, iban: e.target.value.toUpperCase()})} placeholder="IBAN *" className="w-full px-2 py-1.5 rounded-lg border text-xs font-mono" />
                              <input value={addFmSepaForm.bic} onChange={e => setAddFmSepaForm({...addFmSepaForm, bic: e.target.value.toUpperCase()})} placeholder="BIC (optional)" className="w-full px-2 py-1.5 rounded-lg border text-xs font-mono" />
                              {/* iter142: Electronic signature is the wallet JWT itself (matches
                                  MemberRegister guest-upgrade SEPA). No password field. */}
                              <div className="bg-white rounded-lg p-2 border border-blue-200">
                                <p className="font-bold text-[#1e3a5f] mb-1">Elektronische Unterschrift</p>
                                <p className="text-slate-500 mb-1">E-Mail: {data?.customer?.email || ""}</p>
                                <div className="bg-green-50 border border-green-200 rounded-md p-1.5 flex items-start gap-1.5" data-testid="add-fm-sepa-session-signature">
                                  <ShieldCheck className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" />
                                  <p className="text-[11px] text-green-700 leading-snug">Ihre angemeldete Sitzung dient als elektronische Unterschrift / Your logged-in session acts as the electronic signature.</p>
                                </div>
                              </div>
                              <p className="text-green-700 text-center font-medium">{"Keine Geb\u00fchren \u00b7 \u20ac"}{fee.toFixed(2)}/Jahr</p>
                              <button onClick={handleAddFmSepaPayment} disabled={addFmSepaLoading || !upgradeGateOk} className="w-full py-2 rounded-lg bg-blue-600 text-white text-xs font-bold disabled:opacity-50" data-testid="add-fm-sepa-submit">
                                {addFmSepaLoading ? "Wird verarbeitet..." : "SEPA-Mandat unterschreiben"}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* iter142: Back button was linking to a non-existent "agreements"
                            step for the guest-upgrade flow, wiping addFmPendingData and
                            stranding the user. Hide it entirely for the upgrade path;
                            keep it for the (currently unused from this code path) new
                            family member flow. */}
                        {!isGuestUpgrade && (
                          <button onClick={() => { setAddFmStep("agreements"); setAddFmPendingData(null); }} className="w-full py-2 text-xs text-slate-400 hover:underline" data-testid="add-fm-back-btn">Back</button>
                        )}
                      </div>
                        );
                      })()
                    ) : addFmStep === "waiting" ? (
                      <div className="text-center py-4 space-y-3">
                        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f] mx-auto" />
                        <p className="text-sm font-medium text-[#1e3a5f]">Waiting for payment...</p>
                        <p className="text-xs text-slate-500">Complete payment in the Stripe tab.</p>
                        <button onClick={() => { setShowAddFamilyModal(false); resetAddFmState(); }} className="text-xs text-slate-400 underline">Cancel</button>
                      </div>
                    ) : null
                    ) : (
                      <div className="space-y-2">
                        {!linkCodeSent ? (
                          <>
                            <div className="flex gap-2">
                              <input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setHasSearched(false); }} onKeyDown={e => e.key === "Enter" && handleSearchMembers()} placeholder="Search name or email..." className="flex-1 px-3 py-2 rounded-lg border text-sm" data-testid="search-members-input" />
                              <button onClick={handleSearchMembers} disabled={searchLoading || searchQuery.length < 2} className="px-3 py-2 rounded-lg bg-[#1e3a5f] text-white text-xs font-medium disabled:opacity-50" data-testid="search-members-btn">{searchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}</button>
                            </div>
                            {searchResults.length > 0 && (
                              <div className="space-y-2 max-h-32 overflow-y-auto">
                                {searchResults.map(r => {
                                  const rid = r.person_id || r.member_id || r.guest_id;
                                  return (
                                  <div key={`${r.kind || 'member'}-${rid}`} className="bg-white rounded-lg p-3 flex justify-between items-center">
                                    <div>
                                      <p className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                                        {r.name}
                                        {r.kind === "guest" && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold" title="Linking will upgrade this guest to a family member — payment required" data-testid={`wp-kind-guest-${rid}`}>
                                            Guest · needs upgrade
                                          </span>
                                        )}
                                        {r.already_in_other_family && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 font-semibold" title="Already part of another family" data-testid={`wp-in-other-family-${rid}`}>
                                            Already in another family
                                          </span>
                                        )}
                                      </p>
                                      <p className="text-xs text-slate-400">{r.email}</p>
                                    </div>
                                    {r.already_in_family ? (
                                      <span className="text-xs text-green-600 font-medium">In family</span>
                                    ) : r.already_in_other_family ? (
                                      <span className="text-xs text-slate-400 font-medium">Cannot link</span>
                                    ) : r.kind === "guest" ? (
                                      <button onClick={() => handleSendLinkConsent(r)} disabled={linkVerifying} className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium disabled:opacity-50" title="Sends OTP; after verification you'll pay to upgrade this guest to a family member" data-testid={`link-send-${rid}`}>Upgrade &amp; Link</button>
                                    ) : (
                                      <button onClick={() => handleSendLinkConsent(r)} disabled={linkVerifying} className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium disabled:opacity-50" data-testid={`link-send-${rid}`}>Send Consent</button>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                            {searchResults.length === 0 && hasSearched && !searchLoading && (
                              <div
                                className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-center"
                                data-testid="wp-link-search-no-results"
                              >
                                <p className="text-xs font-semibold text-slate-600">No matching members found</p>
                                <p className="text-[11px] text-slate-400 mt-0.5">Try a different name or email, or add them as a new family member instead.</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="space-y-2">
                            <div className="bg-white rounded-lg p-3"><p className="text-sm font-medium">{linkTarget?.name}</p><p className="text-xs text-slate-400">Consent code sent to their email</p></div>
                            <div className="flex gap-2">
                              <input value={linkConsentCode} onChange={e => setLinkConsentCode(e.target.value)} placeholder="6-digit code" maxLength={6} className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono tracking-wider" data-testid="link-consent-input" />
                              <button onClick={handleVerifyLinkConsent} disabled={linkVerifying} className="px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-medium disabled:opacity-50" data-testid="link-verify-btn">{linkVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}</button>
                            </div>
                          </div>
                        )}
                        <button onClick={() => { setShowAddFamilyModal(false); resetAddFmState(); }} className="w-full py-2 rounded-lg bg-slate-200 text-sm font-medium">Cancel</button>
                      </div>
                    )}
                  </div>
                )}

                {familyMembers.length === 0 && !showAddFamilyModal && (
                  <div className="bg-white rounded-2xl p-6 text-center shadow-sm"><p className="text-slate-400 text-sm">No family members yet</p></div>
                )}

                <div className="space-y-3">
                  {familyMembers.map((fm) => (
                    <div key={fm.member_id} className={`bg-white rounded-2xl p-4 shadow-sm ${!fm.is_active ? "opacity-60" : ""}`} data-testid={`family-member-${fm.member_id}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[#1e3a5f] text-sm">{fm.name}</p>
                          <p className="text-slate-400 text-xs">{fm.member_type_name} &bull; {fm.member_id}</p>
                          {fm.email && <p className="text-slate-400 text-xs">{fm.email}</p>}
                          {fm.mobile_number && <p className="text-slate-400 text-xs">{fm.mobile_number}</p>}
                          {fm.address && <p className="text-slate-400 text-xs">{fm.address}</p>}
                          {fm.shares_wallet && <p className="text-xs text-slate-400 mt-0.5">Shared Wallet</p>}
                          {fm.approval_status === "pending" && <p className="text-xs text-amber-600 mt-0.5">Pending FA approval</p>}
                          {fm.approval_status === "incomplete" && (
                            <div className="mt-1 rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5" data-testid={`fm-draft-${fm.member_id}`}>
                              <p className="text-[11px] text-amber-800 font-semibold">Draft — payment incomplete</p>
                              <p className="text-[10px] text-amber-700/90">Complete payment to submit for FA approval, or cancel this draft.</p>
                              <div className="flex gap-1.5 mt-1.5">
                                <button onClick={() => handleResumeFmDraft(fm)} className="px-2 py-1 rounded-md bg-[#1e3a5f] text-white text-[11px] font-semibold" data-testid={`fm-resume-${fm.member_id}`}>Complete payment</button>
                                <button onClick={() => handleCancelFmDraft(fm)} className="px-2 py-1 rounded-md bg-red-500 text-white text-[11px] font-semibold" data-testid={`fm-cancel-draft-${fm.member_id}`}>Cancel draft</button>
                              </div>
                            </div>
                          )}
                          {fm.cancellation_requested && <p className="text-xs text-amber-600 mt-0.5">Cancellation pending</p>}
                          {!fm.is_active && fm.approval_status !== "incomplete" && fm.approval_status !== "pending" && <p className="text-xs text-red-500 mt-0.5">Cancelled</p>}
                        </div>
                        {fm.is_active && !fm.cancellation_requested && (
                          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                            <button onClick={() => startEditProfile(fm.member_id, fm.address, fm.mobile_number)} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-[#1e3a5f] transition-colors" title="Edit contact info" data-testid={`fm-edit-${fm.member_id}`}><Edit2 className="w-4 h-4" /></button>
                            {fm.is_child ? (
                              <button onClick={() => handleFamilyMemberCancel(fm)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" title="Request cancellation" data-testid={`fm-cancel-${fm.member_id}`}><XCircle className="w-4 h-4" /></button>
                            ) : (
                              <button onClick={() => handleFamilyMemberDelink(fm)} className="p-1.5 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors" title="Remove from family" data-testid={`fm-delink-${fm.member_id}`}><Unlink className="w-4 h-4" /></button>
                            )}
                          </div>
                        )}
                      </div>
                      {editingProfile === fm.member_id && (
                        <div className="mt-3 pt-3 border-t space-y-2">
                          <div><label className="text-xs text-slate-500 mb-1 block">Mobile</label><input value={editMobile} onChange={e => setEditMobile(e.target.value)} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid={`edit-fm-mobile-${fm.member_id}`} /></div>
                          <div><label className="text-xs text-slate-500 mb-1 block">Address</label><textarea value={editAddress} onChange={e => setEditAddress(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border text-sm resize-none" data-testid={`edit-fm-address-${fm.member_id}`} /></div>
                          <div className="flex gap-2">
                            <button onClick={handleSaveProfile} disabled={profileSaving} className="flex-1 py-2 rounded-lg bg-[#1e3a5f] text-white text-xs font-medium disabled:opacity-50" data-testid={`save-fm-profile-${fm.member_id}`}>{profileSaving ? "Saving..." : "Save"}</button>
                            <button onClick={() => setEditingProfile(null)} className="flex-1 py-2 rounded-lg bg-slate-200 text-xs font-medium">Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="confirm-dialog-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()} data-testid="confirm-dialog">
            <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">{confirmDialog.title}</h3>
            <p className="text-sm text-slate-600 mb-6">{confirmDialog.message}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-colors"
                data-testid="confirm-dialog-cancel"
              >
                No, go back
              </button>
              <button
                onClick={async () => {
                  const action = confirmDialog.onConfirm;
                  setConfirmDialog(null);
                  await action();
                }}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
                data-testid="confirm-dialog-confirm"
              >
                Yes, proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export const WalletLanding = () => {
  const navigate = useNavigate();
  const { hasModule, tenant, getTenantSlug } = useTenant();

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-slate-100" data-testid="wallet-landing">
      <div className="text-center mb-12">
        <img src={tenant?.logo_url || "/mymaitree-logo.png"} alt={tenant?.name || "My Maitree"} className="w-40 mx-auto mb-6" onError={(e) => { e.target.style.display = 'none'; }} />
        <p className="text-slate-500 italic">{tenant?.tagline || "Your Space. Your Experience. Your Maitree."}</p>
      </div>
      
      <div className="w-full max-w-sm space-y-4">
        <button 
          onClick={() => navigate("/" + _slug() + "/login")} 
          className="w-full py-4 rounded-full bg-[#1e3a5f] text-white font-bold shadow-md hover:shadow-lg transition-shadow"
          data-testid="landing-login"
        >
          Login
        </button>
        {hasModule("guest_portal") && (
        <button 
          onClick={() => navigate("/" + _slug() + "/register")} 
          className="w-full py-4 rounded-full bg-white text-[#1e3a5f] font-bold border-2 border-[#1e3a5f]/20 hover:border-[#1e3a5f]/40 transition-colors"
          data-testid="landing-register"
        >
          Register as Guest
        </button>
        )}
        {hasModule("memberships") && (
        <button 
          onClick={() => navigate("/" + _slug() + "/member-register")} 
          className="w-full py-4 rounded-full bg-white text-[#2d8a4e] font-semibold border-2 border-[#2d8a4e]/20 hover:border-[#2d8a4e]/40 transition-colors"
          data-testid="landing-member-register"
        >
          Join as a Member
        </button>
        )}
      </div>
      
      <p className="absolute bottom-6 text-slate-300 text-sm">
        {tenant?.name || "My Maitree"} - {tenant?.tagline || "Your Space. Your Experience. Your Maitree."}
      </p>
    </div>
  );
};

export default WalletLanding;
  WalletLanding;
